import { useAuth } from "@/hooks/useAuth";
import {
  useGetGitHubInstallations,
  useGitHubAppInstall,
} from "@/hooks/useGitHubApp";
import { PATH } from "@/lib/enums";
import { getCurrentUser } from "@/lib/user/api";
import { useEffect, useState } from "react";
import { redirect } from "react-router-dom";
import LoadingSpinner from "../Common/Spinner";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

// Type definitions
interface Repository {
  id: number;
  name: string;
  full_name: string;
  description: string;
  private: boolean;
}

interface Installation {
  installation_id: number;
  account_login: string;
  account_type: string;
  repository_selection: string;
  repositories?: Repository[];
}
//@ts-ignore
interface InstallationsData {
  installations?: Installation[];
  github_app?: boolean;
}

interface SelectedRepo {
  key: string;
  repo: Repository;
  installationId: number;
}

const GitHubApp: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [selectedRepos, setSelectedRepos] = useState<SelectedRepo[]>([]);
  const [installInProgress, setInstallInProgress] = useState<boolean>(false);
  const { user } = useAuth();
  const installMutation = useGitHubAppInstall();
  const {
    data: installationsData,
    refetch: fetchInstallations,
    isLoading: isLoadingInstallations,
  } = useGetGitHubInstallations(!isConnected, true);

  // Poll for GitHub install status
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    const checkInstallStatus = async () => {
      try {
        const response = await getCurrentUser();
        const backendUser = response.data;

        if (backendUser?.githubInstallInProgress) {
          setInstallInProgress(true);
          // Refresh installations while install is in progress
          fetchInstallations();
        } else if (installInProgress && !backendUser?.githubInstallInProgress) {
          // Install was in progress and now it's complete
          setInstallInProgress(false);
          fetchInstallations();
        }
      } catch (error) {
        // Fallback for not connected
      }
    };

    // Check immediately on mount
    checkInstallStatus();

    // Set up polling interval
    pollInterval = setInterval(() => {
      checkInstallStatus();
    }, 5000); // Poll every 5 seconds

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [installInProgress, fetchInstallations]);

  useEffect(() => {
    // Listen for GitHub App installation success
    const handleInstallSuccess = (event: MessageEvent) => {
      // Security: Validate origin to prevent cross-origin vulnerabilities
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data === "github-app-install-success") {
        setIsConnected(true);

        redirect(PATH.HOME);
        fetchInstallations();
      }
    };

    window.addEventListener("message", handleInstallSuccess);
    return () => window.removeEventListener("message", handleInstallSuccess);
  }, [fetchInstallations]);

  // Check if user is already connected on component mount
  useEffect(() => {
    const checkConnection = async (): Promise<void> => {
      try {
        const result = await fetchInstallations();
        if (result.data?.items && result.data?.items.length > 0) {
          setIsConnected(true);
        }
      } catch (error) {
        // Fallback for not connected
      }
    };

    checkConnection();
  }, [fetchInstallations]);

  useEffect(() => {
    if (installationsData?.items && installationsData?.items.length > 0) {
      setIsConnected(true);
    }
  }, [installationsData]);

  const handleInstallApp = (): void => {
    installMutation.mutate({ userId: user?.uid! });
  };

  const handleRepoSelect = (repo: Repository, installationId: number): void => {
    const repoKey = `${installationId}-${repo.full_name}`;
    setSelectedRepos((prev) => {
      const isSelected = prev.some((r) => r.key === repoKey);
      if (isSelected) {
        return prev.filter((r) => r.key !== repoKey);
      }
      return [...prev, { key: repoKey, repo, installationId }];
    });
  };

  if (isLoadingInstallations) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Card className="holo-card p-10">
          <div className="flex flex-col gap-3">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Connect your GitHub
            </h2>
            <p className="text-gray-600">
              Connect to your GitHub account to deploy your app 🚀
            </p>
          </div>

          <div className="mt-6">
            <Button
              type="button"
              variant="outline"
              className="cyber-button"
              onClick={handleInstallApp}
              disabled={installMutation.isPending}
              aria-busy={installMutation.isPending}
              aria-label="Connect your GitHub account"
            >
              {installMutation.isPending ? "Opening GitHub..." : "Connect"}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 backdrop-blur">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                GitHub Repositories
              </h2>
            </div>

            <Button
              onClick={handleInstallApp}
              className="cyber-button"
              aria-label="Manage"
            >
              Manage
            </Button>
          </div>
        </div>

        <div className="mt-6">
          {isLoadingInstallations ? (
            <div className="space-y-6">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {installationsData?.items?.map((installation: any) => (
                <Card
                  key={installation.installation_id}
                  className="holo-card p-5"
                >
                  <header className="flex items-center gap-3 mb-4">
                    <img
                      src={`https://github.com/${installation.account_login}.png?size=64`}
                      alt={installation.account_login}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full ring-1 ring-gray-200 object-cover"
                      loading="lazy"
                    />
                    <div>
                      <h3 className="font-semibold text-lg sm:text-xl">
                        {installation.account_login}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {installation.account_type} •{" "}
                        {installation.repository_selection} repositories
                      </p>
                    </div>
                  </header>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {installation.repositories?.map((repo: any) => {
                      const repoKey = `${installation.installation_id}-${repo.full_name}`;
                      const isSelected = selectedRepos.some(
                        (r) => r.key === repoKey,
                      );

                      return (
                        <button
                          key={repo.id}
                          type="button"
                          onClick={() =>
                            handleRepoSelect(repo, installation.installation_id)
                          }
                          className={[
                            "group relative w-full text-left rounded-xl border p-4 transition-all",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900",
                            isSelected
                              ? "border-blue-500 bg-blue-50 shadow-sm"
                              : "border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white",
                          ].join(" ")}
                          aria-pressed={isSelected}
                          aria-label={`Select repository ${repo.full_name}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <h4 className="font-medium leading-6 line-clamp-1">
                              {repo.name}
                            </h4>
                            {repo.private && (
                              <span className="shrink-0 text-xs rounded-full px-2 py-0.5 bg-yellow-100 text-yellow-800">
                                Private
                              </span>
                            )}
                          </div>

                          {repo.description && (
                            <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                              {repo.description}
                            </p>
                          )}

                          <p className="mt-2 text-xs text-gray-500 font-mono break-all">
                            {repo.full_name}
                          </p>

                          {isSelected && (
                            <span
                              className="pointer-events-none absolute -top-2 -right-2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white text-xs shadow"
                              aria-hidden="true"
                            >
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GitHubApp;
